/**
 * "Accept as a new account" beside an invoice's bank account finding: the printed account becomes
 * an unverified account of the supplier, never a swap of a verified one (ADR D7).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client/react';
import { Button } from '@insio/ui-toolkit';
import { AcceptInvoiceBankAccountDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { navigation } from '@/navigation';
import { BankAccountError, bankAccountError } from '../utils/bank-account-error';

interface AcceptInvoiceBankAccountActionProps {
  invoiceId: string;
  /** The invoice's supplier party; links to its bank accounts when it already has the account. */
  supplierPartyId?: string | null;
}

/** What the invoice shows once the supplier has the printed account. */
type Outcome = { kind: 'accepted' | 'alreadyThere'; partyId: string | null };

export function AcceptInvoiceBankAccountAction({
  invoiceId,
  supplierPartyId
}: AcceptInvoiceBankAccountActionProps) {
  const t = useFeatureTranslation('partyBankAccounts');
  // The partner's list reads itself again when it opens; the invoice has nothing to refetch.
  const [accept, { loading }] = useMutation(AcceptInvoiceBankAccountDocument, {
    errorPolicy: 'none'
  });
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<BankAccountError | null>(null);

  if (outcome) {
    return (
      <p role='status' className='text-xs text-foreground-base'>
        {outcome.kind === 'accepted' ? t('invoice.accepted') : t('invoice.alreadyThere')}
        {outcome.partyId && (
          <>
            {' '}
            <Link
              to={navigation.partnerBankAccounts(outcome.partyId)}
              className='font-medium underline'
              data-testid='accepted-bank-account-link'
            >
              {t('invoice.openPartner')}
            </Link>
          </>
        )}
      </p>
    );
  }

  const handleAccept = async () => {
    setError(null);
    try {
      const result = await accept({ variables: { input: { invoiceId } } });
      const partyId = result.data?.acceptInvoiceBankAccount.partyId ?? supplierPartyId ?? null;
      setOutcome({ kind: 'accepted', partyId });
    } catch (err) {
      const refusal = bankAccountError(err);
      // The supplier already has the printed account: nothing to accept, only to sign.
      if (refusal === BankAccountError.DUPLICATE) {
        setOutcome({ kind: 'alreadyThere', partyId: supplierPartyId ?? null });
        return;
      }
      setError(refusal);
    }
  };

  return (
    <div className='flex flex-col items-start gap-xs'>
      <Button
        variant='default'
        outline='default'
        size='sm'
        icon='plus'
        disabled={loading}
        testId='accept-invoice-bank-account'
        onClick={() => void handleAccept()}
      >
        {loading ? t('invoice.accepting') : t('invoice.accept')}
      </Button>
      {error && (
        <p role='alert' className='text-xs text-error-text'>
          {t(`errors.${error}`)}
        </p>
      )}
    </div>
  );
}
